import { Change } from '@ui/components/Amount/Change';
import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { usePriceLive } from '@ui/hooks/price/usePriceLive';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import { Box } from 'styled-system/jsx';

import { useDepositCollateralInput } from './hooks/useDepositCollateralInput';
import { useLeverageChangeLatestPrice } from './hooks/useLeverageChange';
import { useLiquidationPriceChange } from './hooks/useLiquidationPriceChange';

export const CollateralChange = ({ position }: { position: Position }) => {
  const price = usePayTokenPriceLatest(position.collateralToken);
  const { input } = useDepositCollateralInput();

  if (!price) return <Box />;
  const collateralUsd = position.getCollateralUsd(price);
  const nextCollateralUsd = collateralUsd.plus(input.value.usd ?? 0);

  return (
    <Change
      containerDataCy="CollateralChange"
      nextValue={nextCollateralUsd}
      prevValue={collateralUsd}
      opts={{
        prefix: '$',
      }}
    />
  );
};
export const CollateralRatioChange = ({ position }: { position: Position }) => {
  const price = usePayTokenPriceLatest(position.collateralToken);
  const { input } = useDepositCollateralInput();
  const pairPrice = usePriceLive(position.pair.baseSymbol);

  if (!price || !pairPrice) return <Box />;
  const collateralUsd = position.getCollateralUsd(price);
  const nextCollateralUsd = collateralUsd.plus(input.value.usd ?? 0);
  const sizeUsd = position.getSizeUsd(pairPrice);

  return (
    <Change
      containerDataCy="CollateralRatioChange"
      nextValue={nextCollateralUsd.div(sizeUsd)}
      prevValue={collateralUsd.div(sizeUsd)}
      opts={{
        isShowSign: false,
        suffix: '%',
      }}
    />
  );
};

export const LeverageChange = ({ position }: { position: Position }) => {
  const change = useLeverageChangeLatestPrice({
    // for displaying, use latest price on leverage is enough
    position,
  });

  if (!change?.prev) return <Box />;

  return (
    <Change
      containerDataCy="LeverageChange"
      nextValue={change.next}
      prevValue={change.prev}
      opts={{
        suffix: 'x',
        displayDecimals: 1,
      }}
    />
  );
};

export const LiquidationPriceChange = ({
  position,
}: {
  position: Position;
}) => {
  const change = useLiquidationPriceChange({ position });
  if (!change?.prev) return <Box />;

  return (
    <Change
      containerDataCy="LiquidationPriceChange"
      nextValue={change.next}
      prevValue={change.prev}
      opts={{
        prefix: '$',
      }}
    />
  );
};
